/**
 * Utility functions for handling mobile image positioning in popups
 * Mobile: screens <= 640px
 * Desktop: screens >= 768px (tablets and larger)
 */

export type ImagePosition = 'left' | 'right' | 'top' | 'bottom';
export type MobileImagePosition = 'top' | 'bottom' | 'hidden';

/**
 * Determines the mobile image position based on popup settings
 * For mobile screens (≤640px):
 * - 'left', 'right', 'top' positions -> show image at top
 * - 'bottom' position -> show image at bottom
 */
export function getMobileImagePosition(originalPosition: ImagePosition): MobileImagePosition {
    if (originalPosition === 'bottom') {
        return 'bottom';
    }
    // For 'left', 'right', 'top' positions, show image at top on mobile
    return 'top';
}

/**
 * Checks if the image should be visible on mobile for the given position
 */
export function shouldShowImageOnMobile(originalPosition: ImagePosition): boolean {
    // We always show images on mobile, just at different positions
    return true;
}

/**
 * Gets the flex direction class for mobile screens based on image position
 */
export function getMobileFlexDirection(originalPosition: ImagePosition): string {
    const mobilePosition = getMobileImagePosition(originalPosition);

    if (mobilePosition === 'top') {
        return 'flex-col'; // Image at top, content below
    } else {
        return 'flex-col-reverse'; // Content at top, image at bottom
    }
}

/**
 * Gets responsive classes for image container on mobile (≤640px)
 */
export function getMobileImageClasses(originalPosition: ImagePosition): string {
    // On mobile, images are always full width with auto height for flexible content
    return 'w-full h-auto flex-shrink-0';
}

/**
 * Gets responsive classes for content container on mobile (≤640px)
 */
export function getMobileContentClasses(originalPosition: ImagePosition): string {
    // On mobile, content takes remaining space with padding
    return 'w-full flex-1 p-3 md:p-6';
}
